import { Component, Input, OnInit } from "@angular/core";
import { BroadcastDetailsService } from "./broadcast-details.service";
import { YouTubeInfoService } from "../../services/youtube-info.service";
import moment from "moment";
import { DomSanitizer } from "@angular/platform-browser";
import OverlayScrollbars from "overlayscrollbars";

@Component({
    selector: "broadcast-details",
    templateUrl: "./broadcast-details.component.html",
    styleUrls: ["./broadcast-details.component.scss"],
})
export class BroadcastDetailsComponent implements OnInit {
    @Input() broadcast;
    @Input() broadcastId;
    @Input() moduleKey;
    @Input() readable;

    public cvAngularDateFormat: string = window["CVAngularDateFormat"];
    public loadingError: string = "";
    public quizError: string = "";
    public extraLoading: boolean = false;
    public showDetails: boolean = false;
    public videoPlayerOpen: boolean = false;
    public quizPopupOpen: boolean = false;
    public progressPopupOpen: boolean = false;

    public player: any = null;
    public imageViewerArray: any = null;
    public imageViewerIndex: number = -1;
    public imageViewerImage: any = null;

    public scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "os-theme-dark",
            visibility: "auto",
        },
    };

    constructor(
        private service: BroadcastDetailsService,
        private youTubeService: YouTubeInfoService,
        private sanitizer: DomSanitizer
    ) {}

    async ngOnInit() {
        if (this.broadcast == null && this.broadcastId != null) {
            const response = await this.service.GetBroadcast(this.broadcastId);

            if (response) {
                // Success
                this.broadcast = response;

                if (this.broadcast.BroadcastType == 0)
                    this.broadcast.TagString = this.broadcast.Tags.map(
                        function (tag) {
                            return tag.TagText;
                        }
                    ).join(", ");

                this.broadcast.VideoInfo = [];
                const youtubeResponse =
                    await this.youTubeService.getYouTubeInformation(
                        this.broadcast.Videos
                    );

                youtubeResponse.forEach((item) => {
                    this.broadcast.VideoInfo.push(item.snippet);
                });
            } else {
                // Error
                this.loadingError = "Error retrieving broadcast";
            }
        }
    }

    ngOnChanges(changes) {
        this.getBroadcastExtraData();
    }

    async getBroadcastExtraData() {
        if (this.broadcast != null) {
            if (this.broadcast.RoleCounts == null) {
                this.extraLoading = true;
                const response = await this.service.GetBroadcastExtraData(
                    this.broadcast.BroadcastID
                );
                if (response) {
                    this.broadcast.CreatedBy = response.createdBy;

                    this.broadcast.RoleCounts = response.roleCounts;
                    this.broadcast.RoleCountString = "";
                    Object.keys(this.broadcast.RoleCounts).forEach(
                        (count, name) => {
                            this.broadcast.RoleCountString +=
                                name + " (" + count + "), ";
                        }
                    );
                    this.broadcast.RoleCountString =
                        this.broadcast.RoleCountString.slice(0, -2);

                    this.broadcast.Quiz = response.quiz;

                    this.extraLoading = false;
                } else {
                    this.broadcast.CreatedBy = "Error";
                    this.broadcast.RoleCounts = { Error: 0 };
                    this.broadcast.Quiz = "Error";

                    this.extraLoading = false;
                }
            }
        }
    }

    readHeight() {
        if (!this.readable || !this.broadcast)
            return "0px"; // No read text box on the admin pages (the '!this.broadcast' check prevents console errors)
        else if (this.broadcast.Read)
            return "36px"; // Broadcast has been read/quiz has been taken
        else return "48px"; // Show button to acknowledge broadcast/take quiz
    }

    async setAsRead() {
        const response = await this.service.SetBroadcastAsRead(
            this.broadcast.BroadcastID
        );
        if (response) this.broadcast.Read = true;
    }

    async toggleFavorite() {
        const response = await this.service.ToggleWorkAidFavoriteStatus(
            this.broadcast.BroadcastID
        );
        if (response)
            // Success
            this.broadcast.Favorite = !this.broadcast.Favorite;
        // Error
        else
            console.error(
                "Failed toggling favorite status for broadcast #" +
                    this.broadcast.BroadcastID
            );
    }

    quizPopupClose() {
        this.quizPopupOpen = false;
        this.quizError = null;
    }

    getRelativeTime(datetime, utcDatetime) {
        const dateCreated = new Date(datetime);
        const utcDateCreated = new Date(utcDatetime);
        let relativeTime;
        if (
            dateCreated.isToday(dateCreated) &&
            dateCreated < new Date().addMinutes(-10)
        )
            relativeTime = utcDateCreated.toString(CVTimeFormat);
        else relativeTime = moment(utcDateCreated).fromNow();
        return relativeTime;
    }

    numberOfRoles() {
        return Object.keys(this.broadcast.RoleCounts).length;
    }

    roleName(index) {
        return !angular.equals(this.broadcast.RoleCounts, {})
            ? Object.keys(this.broadcast.RoleCounts)[index]
            : "None";
    }

    recipientsInRole(index) {
        return !angular.equals(this.broadcast.RoleCounts, {})
            ? this.broadcast.RoleCounts[
                  Object.keys(this.broadcast.RoleCounts)[index]
              ]
            : 0;
    }

    recipientsInAllOtherRoles() {
        let total = 0;
        for (
            let i = 1;
            i < Object.keys(this.broadcast.RoleCounts).length;
            i++
        ) {
            total +=
                this.broadcast.RoleCounts[
                    Object.keys(this.broadcast.RoleCounts)[i]
                ];
        }
        return total;
    }

    broadcastHasNotExpired() {
        if (this.broadcast.ExpirationDate == null) return true;
        return Date.now() < new Date(this.broadcast.ExpirationDate).getTime();
    }

    openPlayer(videoId) {
        this.videoPlayerOpen = true;
        if (this.player != null) {
            if (this.player.playerInfo.videoData.video_id != videoId)
                // Deletes the player so it can be refreshed if a different video has been selected
                this.player.destroy();
            // Same video has been selected, don't mess with the player at all
            else return;
        }

        // Including the time out allows the Youtube IFrame API to get ready 
        setTimeout(() => {
            this.player = new (<any>window).YT.Player("VideoPlayerDirective"  + (this.broadcastId != null ? this.broadcastId : '') + (this.moduleKey != null ? this.moduleKey : ''), {
                height: "100%",
                width: "100%",
                videoId: videoId,
                playerVars: {
                    modestbranding: 1, //minimizes the amount of youtube brand flair in the player
                    playsinline: 0, //sets the playback to be in fullscreen mode (required for iOS)
                    rel: 0, //stops related videos from being shown when the video end       
                    host: 'https://dev.clearviewportal.com'      //this is set as a security feature otherwise a warning about xss will frequently appear in the console
                },
            });
        }, 50);
    }

    videoPlayerClose() {
        this.videoPlayerOpen = false;
        this.player.pauseVideo();
    }

    selectImageForViewer(imageArray, index) {
        this.imageViewerArray = imageArray;
        this.imageViewerIndex = index;
        this.imageViewerImage = imageArray[index];
    }

    // This will be needed when changing the preview that the user sees when viewing the Work Aids assigned to them
    submitAnswers() {
        // ctrl.quizError = null;
        // var questionGroups = ctrl.aff.FormInfo.questionDTOs;
        // var answerGroups = ctrl.aff.FormInfo.answerDTOs;
        // var selectedAnswerGroups = ctrl.aff.FormInfo.selectedAnswerDTOs;
        // var selectedTextAnswerGroups = ctrl.aff.FormInfo.selectedTextAnswerDTOs;
        // var sendObj = {
        //     WorkAidID: ctrl.broadcast.BroadcastID,
        //     ApplicationID: ctrl.broadcast.FormID,
        //     answers: []
        // };
        // var groupedAnswers = [[]];
        // // Loop through each question group
        // for (i = 0; i < questionGroups.length; i++) {
        //     var questions = questionGroups[i];
        //     var selectedAnswers = selectedAnswerGroups[i];
        //     var selectedTextAnswers = selectedTextAnswerGroups[i];
        //     if (i != 0)
        //         groupedAnswers.push([]);
        //     // Loop through each question
        //     for (j = 0; j < questions.length; j++) {
        //         var performValidation = true;
        //         if (questions[j].DependentID != 0) {
        //             // Question is dependent on another question to appear, check if it is active
        //             var parentQuestion = questions.filter(function (o) {
        //                 return o.QuestionID == questions[j].DependentID
        //             })[0];
        //             var indexOfParent = questions.indexOf(parentQuestion);
        //             var parentAnswer = answerGroups[i][indexOfParent].filter(function (o) {
        //                 return o.AnswerID == selectedAnswers[indexOfParent]
        //             })[0];
        //             performValidation = parentAnswer.ShowsDependents;
        //         }
        //         // Validation for whether the question was answered or not
        //         if (performValidation) {
        //             if (questions[j].QuestionType == 3) {
        //                 if (selectedTextAnswers[j] == "") {
        //                     ctrl.quizError = "You must answer all questions";
        //                     return;
        //                 }
        //             }
        //             else {
        //                 if (selectedAnswers[j] == -1) {
        //                     if (questions[j].QuestionType == 4) {
        //                         // Valid checkbox was left unchecked; needs to be assigned value of its "unchecked" AnswerID and not be left as -1 (which means "invalid", not "unchecked")
        //                         selectedAnswers[j] = answerGroups[i][j][1].AnswerID;
        //                     }
        //                     else {
        //                         ctrl.quizError = "You must answer all questions";
        //                         return;
        //                     }
        //                 }
        //             }
        //         }
        //         else {
        //             // Hidden questions must be assigned an empty value
        //             selectedAnswers[j] = -1;
        //             selectedTextAnswers[j] = "";
        //         }
        //         groupedAnswers[i][j] = questions[j].QuestionType == 3 ? selectedTextAnswers[j] : selectedAnswers[j];
        //     }
        // }
        // // Two dimensional-array is the easiest to work with when doing the above compilation and validation, but it needs to be a simple one-dimensional array when passed to the endpoint
        // for (i = 0; i < groupedAnswers.length; i++) {
        //     for (j = 0; j < groupedAnswers[i].length; j++) {
        //         sendObj.answers.push(groupedAnswers[i][j]);
        //     }
        // }
        // ctrl.progressPopupOpen = true;
        // BroadcastDetailsFactory.SubmitQuizAnswers(sendObj).then(
        //     function (data) { // Success
        //         ctrl.progressPopupOpen = false;
        //         ctrl.broadcast.Read = true;
        //         ctrl.quizPopupClose();
        //     },
        //     function (response) { // Error
        //         ctrl.progressPopupOpen = false;
        //         ctrl.quizError = "Error submitting survey/quiz answers";
        //         // TODO: Prevent user from submitting again?
        //     }
        // );
    }

    trustAsHtml(string) {
        return this.sanitizer.bypassSecurityTrustHtml(string);
    }
}

